import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
} from '@angular/core';
import { LocaleService } from '@maximizer/core/shared/data-access';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { AnswerAction } from '../../answer-widgets/answer-widget.model';
import {
  crmCitationTypeKey,
  crmRecordIcon,
} from '../citations/crm-citation.model';
import { formatCitationDate } from '../pipes/crm-citation-ref.pipe';
import { CREATE_LABEL_KEYS } from './suggested-action.labels';
import { AnswerActionWhen } from './suggested-action.service';

/**
 * The whole record a suggested action will create, for the advisor to confirm as classic confirms
 * a CRM action. Create hands the decision back to the cards; nothing here talks to the CRM.
 */
@Component({
  selector: 'ai-suggested-action-dialog',
  templateUrl: './suggested-action-dialog.component.html',
  styleUrl: './suggested-action-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [KENDO_BUTTON, KENDO_DIALOG, TranslatePipe, UI_PIPES],
  host: {
    // The chat's own dialog sits around this one and closes on the same Escape.
    '(keydown.escape)': '$event.stopPropagation()',
  },
})
export class SuggestedActionDialogComponent {
  private readonly locale = inject(LocaleService);

  readonly action = input.required<AnswerAction>();
  readonly when = input.required<AnswerActionWhen>();
  /** The day has gone by: said in words as well as the warning colour. */
  readonly past = input(false);
  /** The entry the record is for, as the CRM names it; null while unknown, or for a personal to-do. */
  readonly name = input<string | null>(null);

  readonly confirmed = output<void>();
  readonly cancelled = output<void>();

  protected readonly titleKey = computed(
    () => CREATE_LABEL_KEYS[this.action().type],
  );
  protected readonly icon = computed(() => crmRecordIcon(this.action().type));
  protected readonly typeKey = computed(() =>
    crmCitationTypeKey(this.action().type),
  );
  protected readonly date = computed(() =>
    formatCitationDate(this.locale, this.when().start, this.when().hasTime),
  );

  constructor() {
    const host = inject<ElementRef<HTMLElement>>(ElementRef);
    // Kendo focuses the primary button once the zone is stable, which a zoneless app never is.
    afterNextRender(() =>
      host.nativeElement
        .querySelector<HTMLButtonElement>('button.ai-action-confirm')
        ?.focus(),
    );
  }
}
